/*
 * Copyright (c) 2024. Dave van Rijn Development
 */
import './style/dvrdSwitch.scss';

import classNames from 'classnames';
import React, {useContext, useMemo} from 'react';
import {ControlContext} from "../util/controlContext";
import {convertColor} from "../util/colorUtil";

interface Props {
    onChange: (value: boolean, evt?: React.MouseEvent | React.ChangeEvent) => void;
    value: boolean;
    className?: string;
    labelClassName?: string;
    disabled?: boolean;
    activeColor?: string;
    trackColor?: string;
    baseColor?: string;
    contrastColor?: string;
    labels?: [string, string]; // Labels placed to the left and right of the switch
    label?: string;  // Label placed to the right of the switch
}

export default function DVRDSwitch(props: Props) {
    const context = useContext(ControlContext);
    const {
        onChange, disabled, value, activeColor, labels, label, trackColor, baseColor, contrastColor,
        labelClassName
    } = props;
    const className = useMemo(() => {
        const names: Array<string | undefined> = ['dvrd-switch', props.className];
        if (!!labels || !!label) names.push('with-labels');
        if (value) names.push('active');
        if (disabled) names.push('disabled');
        return classNames(names);
    }, [props.className, labels, label, value, disabled]);

    function onToggle(evt: React.MouseEvent) {
        if (!disabled)
            onChange(!value, evt);
    }

    function getTrackColor(): string {
        let color: string;
        if (trackColor) color = trackColor;
        else if (disabled) color = 'color-gray-4';
        else if (value) {
            if (activeColor) color = activeColor;
            else color = baseColor ?? context.baseColor;
        } else color = 'color-gray-7';
        return convertColor(color);
    }

    function getHandleColor(): string {
        let color: string;
        if (disabled) color = 'color-gray-3';
        else color = contrastColor ?? context.contrastColor;
        return convertColor(color);
    }

    function renderLabel(position?: 'left' | 'right') {
        const _label = position === 'left' ? labels?.[0] : position === 'right' ? labels?.[1] : label;
        if(!_label) return null;
        return (
            <label className={classNames('dvrd-switch-label', position, labelClassName)}>{_label}</label>
        );
    }

    return (
        <div className={className} onClick={onToggle}>
            {renderLabel('left')}
            <div className='track' style={{backgroundColor: getTrackColor()}}>
                <div className='handle' style={{backgroundColor: getHandleColor()}}/>
            </div>
            {renderLabel('right')}
            {renderLabel()}
        </div>
    )
}